import React from "react";
import { AppContext } from "../../../store/genesys.context";
import { Box, Link, SxProps, Theme, Typography } from "@mui/material";
import StyledPrimaryButton from "../../atoms/buttons/styledPrimaryButton.styled";
import { getDataprotectionLink } from "../../../utils/getDataprotectionLink";
import { reportAppWarning } from "../../../utils/reportAppError";
import {
  getDataProtectionContent,
  LINK_LABEL,
  LINK_PLACEHOLDER,
} from "./dataProtectionContent";
import type { Brand } from "../../../lib/ChatbotConfig";

const linkStyles: SxProps<Theme> = {
  color: (theme) => theme.palette.widgetStyles.dataProtectionPage.linkColor,
  fontSize: "1.0625rem",
  textDecoration: "underline",

  "&:hover": {
    color: (theme) =>
      theme.palette.widgetStyles.dataProtectionPage.linkHoverColor,
  },
};

const textStyles: SxProps<Theme> = {
  color: (theme) => theme.palette.widgetStyles.dataProtectionPage.textColor,
  fontSize: "1.0625rem",
};

/**
 * Splits a paragraph at LINK_PLACEHOLDER and puts the data protection link in
 * its place. Paragraphs without the placeholder come back unchanged.
 */
const renderParagraph = (
  paragraph: string,
  href: string,
): React.ReactNode[] => {
  const segments = paragraph.split(LINK_PLACEHOLDER);

  return segments.flatMap((segment, index) =>
    index === segments.length - 1
      ? [segment]
      : [
          segment,
          <Link
            key={index}
            href={href}
            rel="noreferrer noopener"
            target="_blank"
            sx={linkStyles}
          >
            {LINK_LABEL}
          </Link>,
        ],
  );
};

const DataProtectionNotice = (): React.JSX.Element => {
  const {
    brand,
    handleAcceptDataProtectionNotice,
    trackChatStarted,
  }: {
    brand: Brand;
    handleAcceptDataProtectionNotice: (state: boolean) => void;
    trackChatStarted: () => void;
  } = React.useContext(AppContext);

  const content = getDataProtectionContent(brand);
  const href = getDataprotectionLink(brand);

  const handleAcceptDataProtection = (): void => {
    try {
      // Shared with the skip path in the context, so chat_started is tracked
      // identically whether the notice was accepted or skipped.
      trackChatStarted();
    } catch (error) {
      reportAppWarning({ scope: "dataProtectionNotice.trackChatEvent", error });
    } finally {
      handleAcceptDataProtectionNotice(true);
    }
  };

  return (
    <Box
      sx={{
        padding: "24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "24px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "16px",
        }}
      >
        <Typography
          sx={{
            color: (theme) =>
              theme.palette.widgetStyles.dataProtectionPage.headlineColor,
            fontWeight: "bold",
          }}
          variant="h5"
        >
          {content.headline}
        </Typography>

        {content.paragraphs.map((paragraph, index) => (
          <Typography
            key={index}
            variant="body1"
            color="initial"
            sx={textStyles}
          >
            {renderParagraph(paragraph, href)}
          </Typography>
        ))}

        {content.standaloneLink && (
          <Link
            href={href}
            rel="noreferrer noopener"
            target="_blank"
            sx={linkStyles}
          >
            {LINK_LABEL}
          </Link>
        )}
      </Box>

      <StyledPrimaryButton
        fullWidth
        variant="contained"
        onClick={handleAcceptDataProtection}
      >
        {content.ctaLabel}
      </StyledPrimaryButton>

      <Box sx={{ my: 1 }}></Box>
    </Box>
  );
};

export default DataProtectionNotice;
